import { useState } from 'react';
import { Star, Link as LinkIcon, Trash2, Plus } from 'lucide-react';
import {
  Button,
  Skeleton,
  Card,
  CardBody,
  Badge,
  Rating,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import {
  useTicketFeedbacks,
  useTicketFeedbackTokens,
  useCreateFeedbackToken,
  useRevokeFeedbackToken,
  type TicketFeedbackToken,
} from '@/api/tickets-workflow';
import { formatDateTime } from '@/lib/format';

interface FeedbackTabProps {
  ticketId: number;
}

/* ── Token status badge ───────────────────────────────────────────────────── */

function TokenStatusBadge({ status }: { status: string | null }) {
  if (status === 'used') return <Badge tone="success">Used</Badge>;
  if (status === 'revoked') return <Badge tone="danger">Revoked</Badge>;
  return <Badge tone="info">Active</Badge>;
}

/* ── Token row ────────────────────────────────────────────────────────────── */

function FeedbackTokenRow({
  token,
  onCopy,
  onRevoke,
  isRevoking,
}: {
  token: TicketFeedbackToken;
  onCopy: (url: string) => void;
  onRevoke: (tokenId: number) => void;
  isRevoking: boolean;
}) {
  // The backend stores the raw token string; the feedback_url is only returned
  // on creation. Reconstruct the URL from the token string using window.location.origin
  // so the copy-link button always works, even for pre-existing tokens.
  const feedbackUrl = `${window.location.origin}/feedback/${token.token}`;
  const isRevocable = token.status !== 'revoked' && token.status !== 'used';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-3)',
        flexWrap: 'wrap',
        padding: 'var(--vn-space-2) 0',
        borderBottom: '1px solid var(--vn-border-subtle)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <TokenStatusBadge status={token.status} />
          {token.recipient_name && (
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
              {token.recipient_name}
            </span>
          )}
          {token.recipient_email && (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {token.recipient_email}
            </span>
          )}
        </div>
        <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '4px 0 0' }}>
          Created {token.created_at ? formatDateTime(token.created_at) : '—'}
          {token.expires_at && ` · Expires ${formatDateTime(token.expires_at)}`}
          {token.created_by_name && ` · by ${token.created_by_name}`}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexShrink: 0 }}>
        <Button
          size="sm"
          variant="secondary"
          startIcon={<LinkIcon size={13} />}
          onClick={() => onCopy(feedbackUrl)}
        >
          Copy link
        </Button>
        {isRevocable && (
          <Button
            size="sm"
            variant="ghost"
            startIcon={<Trash2 size={13} />}
            onClick={() => onRevoke(token.token_id)}
            disabled={isRevoking}
          >
            Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

/* ── Main FeedbackTab ─────────────────────────────────────────────────────── */

/**
 * Feedback tab — read-only feedbacks + stats + feedback-token management.
 *
 * Token management (create / copy-link / revoke) is wired to real api-v4
 * endpoints confirmed in @api/modules/tickets/ticket.routes.go:
 *   POST   /tickets/:ticketID/feedback-token
 *   GET    /tickets/:ticketID/feedback-tokens
 *   DELETE /tickets/feedback-token/:tokenID
 *
 * The public /feedback/:token page is out-of-scope (requires a new route).
 * The copy-link button constructs the URL from window.location.origin + token.
 *
 * Tab visibility is controlled by the parent page (hidden unless
 * ticket_type_key === 'error_service' && status in [completed, closed]).
 */
export function FeedbackTab({ ticketId }: FeedbackTabProps) {
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  const feedbacksQuery = useTicketFeedbacks(ticketId);
  const tokensQuery = useTicketFeedbackTokens(ticketId);
  const createMutation = useCreateFeedbackToken(ticketId);
  const revokeMutation = useRevokeFeedbackToken(ticketId);

  const feedbacks = feedbacksQuery.data?.feedbacks ?? [];
  const stats = feedbacksQuery.data?.stats ?? null;
  const tokens = tokensQuery.data?.results ?? [];

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyMsg('Link copied!');
      setTimeout(() => setCopyMsg(null), 2000);
    } catch {
      setCopyMsg('Copy failed — use Ctrl+C');
      setTimeout(() => setCopyMsg(null), 3000);
    }
  };

  const handleCreate = () => {
    createMutation.mutate(
      {},
      {
        onSuccess: (res) => {
          const url = res.result?.feedback_url;
          if (url) {
            void navigator.clipboard.writeText(url).catch(() => undefined);
            setCopyMsg('Token created & link copied!');
            setTimeout(() => setCopyMsg(null), 3000);
          }
        },
      },
    );
  };

  const handleRevoke = (tokenId: number) => {
    revokeMutation.mutate(tokenId);
  };

  if (feedbacksQuery.isLoading || tokensQuery.isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {[1, 2].map((i) => <Skeleton key={i} style={{ height: 80 }} />)}
      </div>
    );
  }

  if (feedbacksQuery.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load feedbacks"
        actions={
          <Button size="sm" variant="ghost" onClick={() => feedbacksQuery.refetch()}>Retry</Button>
        }
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Star size={16} style={{ color: 'var(--vn-fg-muted)' }} />
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          Customer Feedback
        </span>
      </div>

      {/* Copy notification */}
      {copyMsg && (
        <Alert tone="success" title={copyMsg} />
      )}

      {/* Feedback link tokens section */}
      <Section title="Feedback Links">
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {tokensQuery.isError ? (
            <Alert tone="danger" title="Failed to load feedback tokens" />
          ) : (
            <>
              {tokens.length > 0 ? (
                <div>
                  {tokens.map((t) => (
                    <FeedbackTokenRow
                      key={t.token_id}
                      token={t}
                      onCopy={handleCopy}
                      onRevoke={handleRevoke}
                      isRevoking={revokeMutation.isPending}
                    />
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', margin: 0 }}>
                  No feedback links created yet.
                </p>
              )}

              {createMutation.isError && (
                <Alert tone="danger" title="Failed to create feedback token" />
              )}

              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  startIcon={<Plus size={13} />}
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating…' : 'New feedback link'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Stats summary */}
      {stats && stats.total_feedbacks > 0 && (
        <Card flat style={{ borderColor: 'var(--vn-info-border)', background: 'var(--vn-info-subtle)' }}>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}>
                <Rating value={Number(stats.average_rating) || 0} precise readOnly />
                <span
                  style={{
                    fontSize: 'var(--vn-font-size-lg)',
                    fontWeight: 'var(--vn-font-weight-semibold)',
                    color: 'var(--vn-info-text)',
                  }}
                >
                  {stats.average_rating.toFixed(1)} / 5.0
                </span>
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  ({stats.total_feedbacks} {stats.total_feedbacks === 1 ? 'review' : 'reviews'})
                </span>
              </div>

              {/* Dual-rating breakdown when present */}
              {(stats.average_rating_support_quality != null ||
                stats.average_rating_staff_attitude != null) && (
                <div style={{ display: 'flex', gap: 'var(--vn-space-6)', flexWrap: 'wrap' }}>
                  {stats.average_rating_support_quality != null && (
                    <div>
                      <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '0 0 4px' }}>
                        Support Quality
                      </p>
                      <Rating value={Number(stats.average_rating_support_quality) || 0} precise readOnly />
                      <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)', margin: '2px 0 0' }}>
                        {stats.average_rating_support_quality.toFixed(1)} / 5.0
                      </p>
                    </div>
                  )}
                  {stats.average_rating_staff_attitude != null && (
                    <div>
                      <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '0 0 4px' }}>
                        Staff Attitude
                      </p>
                      <Rating value={Number(stats.average_rating_staff_attitude) || 0} precise readOnly />
                      <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)', margin: '2px 0 0' }}>
                        {stats.average_rating_staff_attitude.toFixed(1)} / 5.0
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Feedbacks list */}
      {feedbacks.length === 0 ? (
        <EmptyState
          icon={<Star size={32} />}
          title="No feedback yet"
          description="Customer feedback will appear here once submitted via a feedback link."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {feedbacks.map((fb) => (
            <Card key={fb.feedback_id} flat>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 'var(--vn-space-2)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <Rating value={Number(fb.rating) || 0} readOnly />
                    <span
                      style={{
                        fontSize: 'var(--vn-font-size-sm)',
                        fontWeight: 'var(--vn-font-weight-medium)',
                      }}
                    >
                      {fb.rating}/5
                    </span>
                  </div>
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    {formatDateTime(fb.created_at)}
                  </span>
                </div>

                {/* Dual-rating detail per feedback when present */}
                {(fb.rating_support_quality != null || fb.rating_staff_attitude != null) && (
                  <div style={{ display: 'flex', gap: 'var(--vn-space-6)', flexWrap: 'wrap' }}>
                    {fb.rating_support_quality != null && (
                      <div>
                        <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '0 0 2px' }}>
                          Support Quality
                        </p>
                        <Rating value={Number(fb.rating_support_quality) || 0} readOnly />
                      </div>
                    )}
                    {fb.rating_staff_attitude != null && (
                      <div>
                        <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '0 0 2px' }}>
                          Staff Attitude
                        </p>
                        <Rating value={Number(fb.rating_staff_attitude) || 0} readOnly />
                      </div>
                    )}
                  </div>
                )}

                {fb.feedback_text && (
                  <p
                    style={{
                      fontSize: 'var(--vn-font-size-sm)',
                      color: 'var(--vn-fg-default)',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {fb.feedback_text}
                  </p>
                )}

                {fb.feedback_by_name && (
                  <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: 0 }}>
                    — {fb.feedback_by_name}
                    {fb.feedback_by_email ? ` (${fb.feedback_by_email})` : ''}
                  </p>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
